"use client";

import { offstreetClient } from "@offstreetparking/portal-flow/api";
import { useQueryClient } from "@tanstack/react-query";
import { httpLink } from "@trpc/client";
import { useAccessToken } from "@workos-inc/authkit-nextjs/components";
import { useRef, useState } from "react";
import { useCompany } from "~/components/providers/CompanyProvider";
import { env } from "../../env";

export { offstreetClient };

export function TRPCReactProvider(props: { children: React.ReactNode; token: string | null; locale: string }) {
	const { company } = useCompany();
	const { getAccessToken, refresh } = useAccessToken();
	const queryClient = useQueryClient();

	const sessionRef = useRef({ companyId: company?.id, token: props.token, locale: props.locale });
	sessionRef.current = { companyId: company?.id, token: props.token, locale: props.locale };

	const [trpcClient] = useState(() =>
		offstreetClient.createClient({
			links: [
				httpLink({
					url: `${env.NEXT_PUBLIC_API_URL}/v4`,
					headers: async () => {
						const { companyId, token, locale } = sessionRef.current;
						const liveToken = await getAccessToken().catch(() => undefined);
						const accessToken = liveToken ?? token;
						return {
							"x-company-id": companyId ? String(companyId) : undefined,
							authorization: accessToken ? `Bearer ${accessToken}` : undefined,
							"accept-language": locale,
						};
					},
					fetch: async (url, options) => {
						const response = await fetch(url, options);
						if (response.status === 401 || response.status === 403) {
							const token = await refresh();
							if (token) {
								return fetch(url, {
									...options,
									headers: {
										...options?.headers,
										authorization: `Bearer ${token}`,
									},
								});
							}
						}
						return response;
					},
				}),
			],
		}),
	);

	return (
		<offstreetClient.Provider client={trpcClient} queryClient={queryClient}>
			{props.children}
		</offstreetClient.Provider>
	);
}
